import { v4 as uuidv4 } from 'uuid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

import { AssetDisplayTypeId, FileRestrictionsModel, FormOption } from '@/lib/models';
import { blogsService } from '@/lib/services';
import { useHandleError } from '@/hooks';
import {
	AsyncPage,
	BlogProgramItem,
	BlogProgramItemValue,
	ConfirmDialog,
	LoadingButton,
	TinyMCE,
	TinyMCERef,
} from '@/components';
import { FileUploader, FileUploaderFileValue } from '@/components/file-uploader';

export const BlogPostDetail = () => {
	const { blogId } = useParams<{ blogId?: string }>();
	const computedBlogId = useMemo(() => (blogId ? blogId : uuidv4()), [blogId]);
	const isEditing = useMemo(() => !!blogId, [blogId]);

	const navigate = useNavigate();
	const handleError = useHandleError();
	const blogHtmlEditorRef = useRef<TinyMCERef>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [fileRestrictions, setFileRestrictions] = useState<FileRestrictionsModel>();
	const [programOptions, setProgramOptions] = useState<FormOption[]>([]);
	const [showPublishModal, setShowPublishModal] = useState(false);
	const [showDiscardModal, setShowDiscardModal] = useState(false);
	const [formValues, setFormValues] = useState({
		imageRemoved: false,
		image: undefined as undefined | FileUploaderFileValue,
		blogTitle: '',
		blogText: '',
		programs: [{ id: uuidv4(), programId: '', topicTags: [] }] as BlogProgramItemValue[],
		availability: false,
	});

	const fetchData = useCallback(async () => {
		const response = await blogsService.getFormOptions().fetch();
		setFileRestrictions(response.fileRestrictions);

		const filtersResponse = await blogsService.getMyBlogsFilters().fetch();
		setProgramOptions(filtersResponse.programs ?? []);

		if (!blogId) {
			return;
		}

		const blogResponse = await blogsService.getBlog(blogId).fetch();
		setFormValues({
			imageRemoved: false,
			image: blogResponse.blog.image
				? {
						fileId: blogResponse.blog.image.documentAssetId,
						fileName: blogResponse.blog.image.fileName,
						fileSize: blogResponse.blog.image.fileSize,
						fileSizeDescription: blogResponse.blog.image.fileSizeDescription,
						fileType: blogResponse.blog.image.fileType,
						fileUrl: blogResponse.blog.image.viewUrl,
				  }
				: undefined,
			blogTitle: blogResponse.blog.name,
			blogText: blogResponse.blog.content ?? '',
			programs: blogResponse.blog.masterClientTags
				.flatMap((mct) => mct.programTags)
				.map((pt) => ({
					id: uuidv4(),
					programId: pt.programId,
					topicTags: pt.topicTags,
				})),
			availability: blogResponse.blog.allowPublicView,
		});
	}, [blogId]);

	const handleFormSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setShowPublishModal(true);
	}, []);

	const handlePublish = useCallback(async () => {
		const requestBody = {
			documentId: computedBlogId,
			title: formValues.blogTitle,
			imageRemoved: formValues.imageRemoved,
			content: blogHtmlEditorRef.current?.getContent() ?? '',
			programTags: formValues.programs.map((p) => ({
				programId: p.programId,
				topicTagIds: p.topicTags.map((t) => t.tagId),
			})),
			allowPublicView: formValues.availability,
		};

		setShowPublishModal(false);
		setIsLoading(true);

		try {
			isEditing
				? await blogsService.updateBlog(computedBlogId, requestBody).fetch()
				: await blogsService.createBlog(requestBody).fetch();

			navigate('/blog-posts');
		} catch (error) {
			setIsLoading(false);
			handleError(error);
		}
	}, [
		computedBlogId,
		formValues.availability,
		formValues.blogTitle,
		formValues.imageRemoved,
		formValues.programs,
		handleError,
		isEditing,
		navigate,
	]);

	useEffect(() => {
		const selectedProgramIds = formValues.programs.map((p) => p.programId).filter((pId) => pId);

		setProgramOptions((previousValue) => {
			return previousValue.map((programOption) => {
				if (selectedProgramIds.includes(programOption.id)) {
					programOption.disabled = true;
				} else {
					programOption.disabled = false;
				}

				return programOption;
			});
		});
	}, [formValues.programs]);

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Blog Posts - Create</title>
			</Helmet>

			<ConfirmDialog
				show={showPublishModal}
				onHide={() => {
					setShowPublishModal(false);
				}}
				onConfirm={handlePublish}
				title="Publish Post"
				confirmText="Publish"
				cancelText="Cancel"
				disabled={isLoading}
			>
				<p className="mb-0">
					Are you sure you want to publish your blog post? Your post will become live on Hackett Connect<sup>&reg;</sup> immediately.
				</p>
			</ConfirmDialog>

			<ConfirmDialog
				show={showDiscardModal}
				onHide={() => {
					setShowDiscardModal(false);
				}}
				onConfirm={() => {
					navigate(-1);
				}}
				title="Discard Post"
				confirmText="Discard"
				cancelText="Cancel"
			>
				<p className="mb-0">
					You haven't finished your post yet. Are you sure you want to leave and discard your work?
				</p>
			</ConfirmDialog>

			<Container className="py-11">
				<Form onSubmit={handleFormSubmit}>
					<fieldset disabled={isLoading}>
						<Row>
							<Col xs={12} xl={8} className="mb-6">
								<Form.Group className="mb-6">
									<Form.Label>Blog Title</Form.Label>
									<Form.Control
										type="text"
										value={formValues.blogTitle}
										onChange={({ currentTarget }) => {
											setFormValues((previousValue) => ({
												...previousValue,
												blogTitle: currentTarget.value,
											}));
										}}
									/>
								</Form.Group>
								{fileRestrictions && (
									<FileUploader
										value={formValues.image}
										accept={{
											'': [
												...fileRestrictions.imageFileFormats
													.split(',')
													.map((ext) => `.${ext}`.toLowerCase()),
											],
										}}
										fileRestrictions={fileRestrictions}
										presignedUploadGetter={({ fileId, fileName, fileType, fileSize }) =>
											blogsService.getPresignedUpload(computedBlogId, {
												assetDisplayTypeId: AssetDisplayTypeId.HERO,
												associatedRecordId: computedBlogId,
												fileId,
												fileName,
												fileSize,
												fileType,
											})
										}
										uploadStatusUpdater={(upload, uploadStatusId) =>
											blogsService.updateAssetStatus(computedBlogId, upload.fileId, {
												uploadStatusId,
											})
										}
										onChange={({ fileRemoved, ...rest }) => {
											if (fileRemoved) {
												setFormValues((previousValue) => ({
													...previousValue,
													imageRemoved: true,
													image: undefined,
												}));
											} else {
												setFormValues((previousValue) => ({
													...previousValue,
													imageRemoved: false,
													image: rest,
												}));
											}
										}}
									/>
								)}
								<Form.Group>
									<Form.Label>Blog Text</Form.Label>
									<TinyMCE
										ref={blogHtmlEditorRef}
										initialValue={formValues.blogText}
										blogToolbar
										presignedUploadGetter={({ fileId, fileName, fileType, fileSize }) =>
											blogsService.getPresignedUpload(computedBlogId, {
												assetDisplayTypeId: AssetDisplayTypeId.INLINE,
												associatedRecordId: computedBlogId,
												fileId,
												fileName,
												fileSize,
												fileType,
											})
										}
										uploadStatusUpdater={(upload, uploadStatusId) =>
											blogsService.updateAssetStatus(computedBlogId, upload.fileId, {
												uploadStatusId,
											})
										}
									/>
								</Form.Group>
							</Col>
							<Col xs={12} xl={4}>
								<Card className="mb-2">
									<Card.Body>
										<LoadingButton
											type="submit"
											className="mb-6 d-block w-100"
											isLoading={isLoading}
										>
											{blogId ? 'Publish Updates' : 'Publish'}
										</LoadingButton>
										<Button
											type="button"
											className="d-block w-100"
											variant="outline-primary"
											onClick={() => {
												if (blogId) {
													navigate(-1);
												} else {
													setShowDiscardModal(true);
												}
											}}
										>
											{blogId ? 'Cancel Editing' : 'Discard Post'}
										</Button>
									</Card.Body>
								</Card>
								<Card className="mb-2">
									<Card.Body>
										<h3 className="mb-4">Programs</h3>
										{formValues.programs.map((program, programIndex) => {
											const isLast = formValues.programs.length - 1 === programIndex;

											return (
												<React.Fragment key={program.id}>
													<BlogProgramItem
														className={classNames({ 'mb-6': !isLast })}
														programOptions={programOptions}
														value={program}
														onChange={(newProgram) => {
															setFormValues((previousValue) => ({
																...previousValue,
																programs: previousValue.programs.map((p) =>
																	p.id === program.id ? newProgram : p
																),
															}));
														}}
														showDeleteButton={formValues.programs.length > 1}
														onDeleteButtonClick={() => {
															setFormValues((previousValue) => ({
																...previousValue,
																programs: previousValue.programs.filter(
																	(p) => p.id !== program.id
																),
															}));
														}}
													/>
													{!isLast && <hr className="mb-4" />}
												</React.Fragment>
											);
										})}
										{formValues.programs.length < programOptions.length && (
											<div className="pt-6 text-right">
												<Button
													onClick={() => {
														setFormValues((previousValue) => ({
															...previousValue,
															programs: [
																...previousValue.programs,
																{ id: uuidv4(), programId: '', topicTags: [] },
															],
														}));
													}}
												>
													Add Program
												</Button>
											</div>
										)}
									</Card.Body>
								</Card>
								<Card>
									<Card.Body>
										<Form.Group>
											<Form.Check
												type="checkbox"
												name="availability"
												id="availability"
												label="Post is available for public viewing"
												checked={formValues.availability}
												onChange={(e) => {
													setFormValues((previousValue) => ({
														...previousValue,
														availability: e.target.checked,
													}));
												}}
											/>
										</Form.Group>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					</fieldset>
				</Form>
			</Container>
		</AsyncPage>
	);
};
